import { http } from 'app/lib/axios';
export type ImageVariant = 'mini' | 'small' | 'medium' | 'large';

const IMAGE_VARIANT_SUFFIX_REGEX = /__(mini|small|medium|large)(?=\.[^./?#]+(?:[?#].*)?$|$|[?#])/;
const attemptedVariantRebuilds = new Set<string>();

function appendVariantSuffix(key: string, variant: ImageVariant): string {
  const lastSlashIndex = key.lastIndexOf('/');
  const directory = lastSlashIndex >= 0 ? key.slice(0, lastSlashIndex + 1) : '';
  const fileName = lastSlashIndex >= 0 ? key.slice(lastSlashIndex + 1) : key;
  const extensionIndex = fileName.lastIndexOf('.');

  if (extensionIndex > 0) {
    return `${directory}${fileName.slice(0, extensionIndex)}__${variant}${fileName.slice(extensionIndex)}`;
  }

  return `${key}__${variant}`;
}

function stripVariantSuffix(key: string): string {
  return key.replace(/__(mini|small|medium|large)(?=\.[^./]+$|$)/, '');
}

export function getImageVariantForSize(width: number, height: number): ImageVariant {
  const longestEdge = Math.max(width, height);
  if (longestEdge <= 200) return 'mini';
  if (longestEdge <= 400) return 'small';
  if (longestEdge <= 800) return 'medium';
  return 'large';
}

export function getImageVariantUrl(url: string | null | undefined, variant: ImageVariant): string | undefined {
  if (typeof url !== 'string') return undefined;
  if (!url.startsWith('http://') && !url.startsWith('https://')) return url;
  if (url.includes('.svg')) return url;
  if (url.includes('default/media') && !url.includes('.svg')) return url;
  if (IMAGE_VARIANT_SUFFIX_REGEX.test(url)) return url; // URL already has a recognized variant suffix
  if (url.includes('avatar.png')) return url; // Special case for default avatar, which shouldn't have variants

  try {
    const parsed = new URL(url);
    parsed.pathname = appendVariantSuffix(stripVariantSuffix(parsed.pathname), variant);
    return parsed.toString();
  } catch {
    const [urlWithoutHash = '', hash = ''] = url.split('#');
    const [path = '', query = ''] = urlWithoutHash.split('?');

    return `${appendVariantSuffix(stripVariantSuffix(path), variant)}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
  }
}

export function getMasterImageUrl(url: string | null | undefined): string | undefined {
  if (typeof url !== 'string') return undefined;
  if (!url.startsWith('http://') && !url.startsWith('https://')) return undefined;
  if (!IMAGE_VARIANT_SUFFIX_REGEX.test(url)) return url;

  try {
    const parsed = new URL(url);
    parsed.pathname = stripVariantSuffix(parsed.pathname);
    return parsed.toString();
  } catch {
    const [urlWithoutHash = '', hash = ''] = url.split('#');
    const [path = '', query = ''] = urlWithoutHash.split('?');

    return `${stripVariantSuffix(path)}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
  }
}

export function triggerCreateImageVariantsFromUrl(url: string | null | undefined): void {
  const masterUrl = getMasterImageUrl(url);
  if (!masterUrl) return;
  if (masterUrl.includes('.svg')) return;
  if (masterUrl.includes('default/media')) return;
  if (masterUrl.includes('avatar.png')) return;
  if (attemptedVariantRebuilds.has(masterUrl)) return;

  attemptedVariantRebuilds.add(masterUrl);

  void http
    .post('/listings/createImageVariantsFromUrl', {
      url: masterUrl,
    })
    .catch(() => {
      attemptedVariantRebuilds.delete(masterUrl);
    });
}
